import { Link } from 'react-router-dom';
import { useEffect, useState, useCallback } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { aboutContent } from '@/mocks/pagesData';

const FORM_URL = 'https://readdy.ai/api/form/d9q9etcnlsngrm4lltr0';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleFormSubmit = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const form = e.currentTarget;
    const honeypotEl = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypotEl && honeypotEl.value.trim() !== '') {
      setFormStatus('success');
      setTimeout(() => setFormStatus('idle'), 3000);
      return;
    }

    setFormStatus('loading');
    setFormError('');

    try {
      const formData = new FormData(form);
      const response = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 3000);
      } else {
        const serverMsg = (parsed?.meta as Record<string, string>)?.message || (parsed?.meta as Record<string, string>)?.detail || responseText || '提交失败，请稍后重试';
        if (typeof serverMsg === 'string' && (serverMsg.includes('spam') || serverMsg.includes('form data is spam'))) {
          setFormError('提交失败，请稍后重试');
        } else {
          setFormError(serverMsg);
        }
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查网络连接后重试');
      setFormStatus('error');
    }
  }, []);

  return (
    <>
      <Navbar />
      <main className="pt-20">
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="max-w-4xl mx-auto px-4 md:px-6 py-6">
            <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-3" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">首页</Link>
              <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center"></i>
              <span className="text-foreground-700 font-medium">关于我们</span>
            </nav>
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">关于我们</h1>
          </div>
        </div>

        {/* Brand Introduction */}
        <section className="py-10 md:py-16 px-4 md:px-6">
          <div className="max-w-5xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-10 items-center">
              <div className="flex-1">
                <span className="text-xs font-medium text-primary-500 uppercase tracking-wider">品牌介绍</span>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mt-2 mb-4">
                  {aboutContent.brandIntro.subtitle}
                </h2>
                {aboutContent.brandIntro.content.split('\n\n').map((p, i) => (
                  <p key={i} className="text-sm text-foreground-600 leading-relaxed mb-4">
                    {p.trim()}
                  </p>
                ))}
              </div>
              <div className="flex-shrink-0 w-full lg:w-[420px]">
                <img
                  src={aboutContent.brandIntro.image}
                  alt="麻豆传媒团队"
                  title="麻豆传媒专业团队"
                  className="w-full aspect-[3/2] rounded-lg object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Milestones */}
        <section className="py-10 md:py-16 px-4 md:px-6 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <span className="text-xs font-medium text-accent-600 uppercase tracking-wider">发展历程</span>
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mt-2">
                品牌里程碑
              </h2>
            </div>
            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-accent-200 md:-translate-x-px"></div>
              {aboutContent.milestones.map((m, i) => (
                <div key={m.year} className={`relative flex items-start mb-8 ${i % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                  <div className="hidden md:flex flex-1"></div>
                  <div className="absolute left-2.5 md:left-1/2 w-3 h-3 rounded-full bg-accent-500 -translate-x-1.5 md:-translate-x-1.5 mt-1.5 z-10"></div>
                  <div className="ml-10 md:ml-0 md:flex-1 md:px-8">
                    <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
                      <span className="text-xs font-bold text-accent-600">{m.year}</span>
                      <p className="text-sm text-foreground-600 mt-1">{m.event}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-10 md:py-16 px-4 md:px-6">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10">
              <span className="text-xs font-medium text-primary-500 uppercase tracking-wider">口碑评价</span>
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mt-2">
                行业认可与用户评价
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {aboutContent.testimonials.map((t) => (
                <div key={t.name} className="bg-background-50 border border-background-200/70 rounded-lg p-5">
                  <div className="flex items-center gap-1 mb-3">
                    {[...Array(5)].map((_, i) => (
                      <div key={i} className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-star-fill text-sm text-accent-500"></i>
                      </div>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">{t.content}</p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                      <span className="text-sm font-bold text-primary-600">{t.name[0]}</span>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground-800">{t.name}</p>
                      <p className="text-xs text-foreground-400">{t.role}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ on About page */}
        <section className="py-10 md:py-16 px-4 md:px-6 bg-background-100">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-10">
              <span className="text-xs font-medium text-secondary-600 uppercase tracking-wider">常见问题</span>
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mt-2">
                关于我们你想知道的
              </h2>
            </div>
            <div className="flex flex-col gap-3">
              {[
                { q: '麻豆传媒的团队规模如何？', a: '麻豆传媒目前拥有超过300人的专业团队，涵盖内容制作、技术研发、市场运营等核心部门。公司在北京、上海、广州、成都设有分支机构，汇聚了来自影视、互联网和科技领域的顶尖人才。' },
                { q: '麻豆传媒的商业模式是什么？', a: '麻豆传媒采用会员订阅与内容授权相结合的双轮驱动模式。用户可以通过月度或年度会员订阅享受海量内容，同时我们也与各大平台和电视台合作进行内容分发，扩大品牌影响力。' },
                { q: '如何与麻豆传媒合作？', a: '我们欢迎各类内容创作者、制作公司和品牌方与我们合作。您可以通过本页面的联系表单或直接发送邮件至商务合作邮箱，我们的团队将尽快与您取得联系并探讨合作方案。' },
                { q: '麻豆传媒的内容审核机制是怎样的？', a: '麻豆传媒设有严格的内容审核与品质把控体系，所有上线内容均需经过剧本评审、样片审核、成片检测三道关卡，确保内容符合国家法律法规和社会主义核心价值观，同时保持高品质的艺术水准。' },
              ].map((item, i) => (
                <div key={i} className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setOpenFAQ(openFAQ === i ? null : i)}
                    className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer"
                  >
                    <span className="text-sm font-medium text-foreground-900 pr-4">{item.q}</span>
                    <div className={`w-6 h-6 flex items-center justify-center flex-shrink-0 transition-transform duration-300 ${openFAQ === i ? 'rotate-45' : ''}`}>
                      <i className="ri-add-line text-lg text-foreground-400"></i>
                    </div>
                  </button>
                  <div className={`transition-all duration-300 overflow-hidden ${openFAQ === i ? 'max-h-96 pb-5' : 'max-h-0'}`}>
                    <p className="px-5 text-sm text-foreground-600 leading-relaxed">{item.a}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="py-10 md:py-16 px-4 md:px-6">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10">
              <span className="text-xs font-medium text-primary-500 uppercase tracking-wider">联系我们</span>
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mt-2">
                期待与您的交流
              </h2>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              <div>
                <div className="space-y-5">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 flex-shrink-0">
                      <i className="ri-map-pin-line text-primary-500 text-sm w-4 h-4 flex items-center justify-center"></i>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground-800 mb-1">公司地址</p>
                      <p className="text-sm text-foreground-500">{aboutContent.contact.address}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 flex-shrink-0">
                      <i className="ri-phone-line text-primary-500 text-sm w-4 h-4 flex items-center justify-center"></i>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground-800 mb-1">联系电话</p>
                      <p className="text-sm text-foreground-500">{aboutContent.contact.phone}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 flex-shrink-0">
                      <i className="ri-mail-line text-primary-500 text-sm w-4 h-4 flex items-center justify-center"></i>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground-800 mb-1">电子邮箱</p>
                      <p className="text-sm text-foreground-500">{aboutContent.contact.email}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 flex-shrink-0">
                      <i className="ri-wechat-line text-primary-500 text-sm w-4 h-4 flex items-center justify-center"></i>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground-800 mb-1">微信公众号</p>
                      <p className="text-sm text-foreground-500">{aboutContent.contact.wechat}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <div className="bg-background-50 border border-background-200/70 rounded-lg p-6">
                  <h3 className="text-base font-heading font-bold text-foreground-950 mb-4">发送消息</h3>
                  {formStatus === 'success' ? (
                    <div className="text-center py-8">
                      <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-accent-100 mb-3">
                        <i className="ri-check-line text-2xl text-accent-600"></i>
                      </div>
                      <p className="text-sm font-semibold text-foreground-800">消息已发送！</p>
                      <p className="text-xs text-foreground-500 mt-1">我们会尽快与您联系</p>
                    </div>
                  ) : (
                    <form onSubmit={handleFormSubmit} data-readdy-form="" className="space-y-4">
                      <div>
                        <label htmlFor="about-name" className="block text-xs font-medium text-foreground-700 mb-1.5">姓名</label>
                        <input
                          type="text"
                          id="about-name"
                          name="name"
                          required
                          className="w-full px-3 py-2.5 rounded-lg border border-background-200 bg-white text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-all"
                          placeholder="请输入您的姓名"
                        />
                      </div>
                      <div>
                        <label htmlFor="about-email" className="block text-xs font-medium text-foreground-700 mb-1.5">邮箱</label>
                        <input
                          type="email"
                          id="about-email"
                          name="email"
                          required
                          className="w-full px-3 py-2.5 rounded-lg border border-background-200 bg-white text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-all"
                          placeholder="请输入您的邮箱"
                        />
                      </div>
                      <div>
                        <label htmlFor="about-message" className="block text-xs font-medium text-foreground-700 mb-1.5">留言内容</label>
                        <textarea
                          id="about-message"
                          name="message"
                          required
                          maxLength={500}
                          rows={4}
                          className="w-full px-3 py-2.5 rounded-lg border border-background-200 bg-white text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-all resize-none"
                          placeholder="请输入您的留言（500字以内）"
                        ></textarea>
                        <p className="text-xs text-foreground-400 mt-1">限500字以内</p>
                      </div>
                      {/* Honeypot */}
                      <div className="form-honeypot">
                        <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                      </div>
                      {formStatus === 'error' && formError && (
                        <p className="text-xs text-red-500">{formError}</p>
                      )}
                      <button
                        type="submit"
                        disabled={formStatus === 'loading'}
                        className="w-full py-2.5 rounded-lg bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-all duration-200 disabled:opacity-60 cursor-pointer whitespace-nowrap"
                      >
                        {formStatus === 'loading' ? '发送中...' : '发送消息'}
                      </button>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: '关于麻豆传媒',
            description: aboutContent.brandIntro.subtitle,
            url: 'https://madoumedia.com/about',
            isPartOf: {
              '@type': 'WebSite',
              name: '麻豆传媒',
              url: 'https://madoumedia.com/',
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: [
              { '@type': 'Question', name: '麻豆传媒的团队规模如何？', acceptedAnswer: { '@type': 'Answer', text: '麻豆传媒目前拥有超过300人的专业团队，涵盖内容制作、技术研发、市场运营等核心部门。公司在北京、上海、广州、成都设有分支机构，汇聚了来自影视、互联网和科技领域的顶尖人才。' } },
              { '@type': 'Question', name: '麻豆传媒的商业模式是什么？', acceptedAnswer: { '@type': 'Answer', text: '麻豆传媒采用会员订阅与内容授权相结合的双轮驱动模式。用户可以通过月度或年度会员订阅享受海量内容，同时我们也与各大平台和电视台合作进行内容分发，扩大品牌影响力。' } },
              { '@type': 'Question', name: '如何与麻豆传媒合作？', acceptedAnswer: { '@type': 'Answer', text: '我们欢迎各类内容创作者、制作公司和品牌方与我们合作。您可以通过本页面的联系表单或直接发送邮件至商务合作邮箱，我们的团队将尽快与您取得联系并探讨合作方案。' } },
              { '@type': 'Question', name: '麻豆传媒的内容审核机制是怎样的？', acceptedAnswer: { '@type': 'Answer', text: '麻豆传媒设有严格的内容审核与品质把控体系，所有上线内容均需经过剧本评审、样片审核、成片检测三道关卡，确保内容符合国家法律法规和社会主义核心价值观，同时保持高品质的艺术水准。' } },
            ],
          }),
        }}
      />
    </>
  );
}